/**
 * Phase-of-play taxonomy — primary slicing of drills and sessions.
 *
 * Decision: Founder sync 2026-04-30 (Peter / Josh / Ben Petty).
 * Supersedes the prior 4-Corner Model as the PRIMARY drill organising
 * axis. Corners remain canonical for rating, development tracking,
 * and welfare KPIs — see `corners.ts` — but are SECONDARY for drills,
 * sessions, and curriculum planning.
 *
 * The UI primary navigation, AI session-planner generation prompts,
 * and curriculum coverage analytics all key off this taxonomy. Each
 * Drill carries a `primaryPhase` tag plus a `cornerEmphasis` profile
 * (the cross-cutting secondary axis).
 *
 * Why four phases: in coaching practice these are the moments that
 * define what a team is trying to do. "Attacking" and "Defending"
 * are obvious; "Transitions" is the moment of turnover (attack →
 * defence, or defence → attack); "Individual" is player-development
 * work that crosses all three phase contexts (1v1, technique, decision-
 * making in isolation, mental skills).
 */

export const PHASES_OF_PLAY = ['attacking', 'defending', 'transitions', 'individual'] as const;
export type PhaseOfPlay = typeof PHASES_OF_PLAY[number];

export interface PhaseOfPlayMeta {
  id: PhaseOfPlay;
  label: string;
  description: string;
  /** Default colour when surfaced in chips, scrubber markers, etc.
   *  Distinct from the four-corner colours so coaches can scan a
   *  session card at a glance and see both axes simultaneously. */
  colorHex: string;
  /** Single-character glyph for compact chips. */
  glyph: string;
}

export const PHASE_OF_PLAY_META: Record<PhaseOfPlay, PhaseOfPlayMeta> = {
  attacking: {
    id: 'attacking',
    label: 'Attacking',
    description: 'In possession — building, progressing, finishing.',
    colorHex: '#E8763B',
    glyph: '↑',
  },
  defending: {
    id: 'defending',
    label: 'Defending',
    description: 'Out of possession — pressing, blocking, recovering.',
    colorHex: '#2D7DD2',
    glyph: '↓',
  },
  transitions: {
    id: 'transitions',
    label: 'Transitions',
    description: 'Moments of turnover — attack→defence and defence→attack.',
    colorHex: '#9B59B6',
    glyph: '↔',
  },
  individual: {
    id: 'individual',
    label: 'Individual',
    description: 'Player-development work that crosses all phases — 1v1, technique, decision-making, mental skills.',
    colorHex: '#16A085',
    glyph: '●',
  },
};

/** Common sub-themes inside each primary phase. Not exhaustive — the
 *  AI session planner is free to invent more — but useful as priors
 *  when building UI chips and as drop-down hints. */
export const PHASE_SUB_THEMES: Record<PhaseOfPlay, string[]> = {
  attacking: [
    'build-up',
    'breaking-lines',
    'width',
    'depth',
    'crossing',
    'finishing',
    'switching-play',
    'third-man',
    'overloads',
    'set-pieces-attacking',
    'counter-attacking',
  ],
  defending: [
    'pressing',
    'counter-pressing',
    'low-block',
    'mid-block',
    'high-block',
    'defensive-shape',
    '1v1-defending',
    'recovery-runs',
    'set-pieces-defending',
    'compactness',
  ],
  transitions: [
    'attack-to-defence',
    'defence-to-attack',
    'rest-defence',
    'turnover-reactions',
    'first-action-after-loss',
    'first-action-after-recovery',
  ],
  individual: [
    '1v1-attack',
    '1v1-defence',
    'first-touch',
    'passing-technique',
    'finishing-technique',
    'scanning',
    'decision-making',
    'composure-under-pressure',
    'goalkeeping',
  ],
};

/** Heuristic: given a free-text user instruction (typically the
 *  primaryFocus from a SessionPlanInput), return the most likely
 *  phase. Returns null when nothing matches confidently — the AI
 *  fallback then asks the coach to pick. */
export function inferPhaseOfPlay(text: string): PhaseOfPlay | null {
  const t = text.toLowerCase();
  // Order matters — check sub-themes that are unambiguous before
  // falling through to broad phase keywords.
  if (/\b(press|counter-press|low.?block|mid.?block|high.?block|defensive shape|recovery run|defend)/.test(t)) {
    return 'defending';
  }
  if (/\b(transition|turnover|after.?loss|after.?recovery|rest defen|second.?ball)/.test(t)) {
    return 'transitions';
  }
  if (/\b(1v1|first touch|technique|scan|composure|decision.?making|individual)/.test(t)) {
    return 'individual';
  }
  if (/\b(attack|build.?up|break.?lines|width|depth|cross|finish|third.?man|switch|overload)/.test(t)) {
    return 'attacking';
  }
  return null;
}

/** Coverage profile for a session — proportion of session minutes
 *  spent in each phase. Used in the planner output to mirror the
 *  existing corner_coverage shape. Need not sum to exactly 100 — the
 *  rendering layer normalises. */
export interface PhaseCoverage {
  attacking: number;
  defending: number;
  transitions: number;
  individual: number;
}

export const PHASE_COVERAGE_KEYS: ReadonlyArray<keyof PhaseCoverage> =
  ['attacking', 'defending', 'transitions', 'individual'];

/** Validate that a phase coverage object roughly sums to 100.
 *  Returns the normalised version with the requested tolerance. */
export function normalisePhaseCoverage(c: PhaseCoverage, tolerance = 2): PhaseCoverage {
  const sum = c.attacking + c.defending + c.transitions + c.individual;
  if (Math.abs(sum - 100) <= tolerance || sum <= 0) return c;
  const factor = 100 / sum;
  return {
    attacking: Math.round(c.attacking * factor),
    defending: Math.round(c.defending * factor),
    transitions: Math.round(c.transitions * factor),
    individual: Math.round(c.individual * factor),
  };
}

// ============================================================================
// Tier-aware taxonomy — Ben Petty voice note 2026-05-05 (afternoon, note 1).
//
// At grassroots, coaches use THREE phases — in possession / out of possession /
// transition — not the four-phase academy taxonomy. The single-transition
// framing is deliberate: at grassroots the turnover frequency is so high that
// splitting attack→defend and defend→attack into separate themes confuses the
// coach without adding clarity. "Individual" doesn't exist as a separate phase
// at grassroots either — decision-making and technique work happens INSIDE
// the possession phases (per note 3, "decision-making mandatory in technical
// drills"; physical "emerges from session shape, not separate fitness blocks").
//
// Storage stays as the 4-phase academy taxonomy (drills.primary_phase). The
// projection helpers below map between the two so a grassroots coach sees
// 3 chips and an academy coach sees 4, both surfacing the same drill catalogue.
// ============================================================================

export type CoachingTier = 'grassroots' | 'academy';

export const GRASSROOTS_PHASES = ['in_possession', 'out_of_possession', 'transition'] as const;
export type GrassrootsPhase = typeof GRASSROOTS_PHASES[number];

export interface GrassrootsPhaseMeta {
  id: GrassrootsPhase;
  label: string;
  description: string;
  colorHex: string;
  glyph: string;
}

export const GRASSROOTS_PHASE_META: Record<GrassrootsPhase, GrassrootsPhaseMeta> = {
  in_possession: {
    id: 'in_possession',
    label: 'In possession',
    description: 'When we have the ball — keep it, move it, score with it.',
    colorHex: '#E8763B',
    glyph: '↑',
  },
  out_of_possession: {
    id: 'out_of_possession',
    label: 'Out of possession',
    description: "When the other team has the ball — get it back.",
    colorHex: '#2D7DD2',
    glyph: '↓',
  },
  transition: {
    id: 'transition',
    label: 'Transition',
    description: 'The moment of turnover — what happens in the seconds after we win or lose the ball.',
    colorHex: '#9B59B6',
    glyph: '↔',
  },
};

/** Map a `users.coach_level` value to the coaching tier we use for taxonomy
 *  selection. NOVICE + GRASSROOTS coaches see the 3-phase grassroots view;
 *  COMPETITIVE / ADVANCED / ELITE_CAT1 see the 4-phase academy view. Null /
 *  unknown defaults to grassroots — the simpler view is the safer default. */
export function tierForCoachLevel(level: string | null | undefined): CoachingTier {
  if (level === 'COMPETITIVE' || level === 'ADVANCED' || level === 'ELITE_CAT1') {
    return 'academy';
  }
  return 'grassroots';
}

/** Project a stored 4-phase academy phase into the grassroots view. Lossy
 *  for the `individual` phase — at grassroots there's no separate individual
 *  bucket, so individual drills surface under in_possession (where most
 *  individual technique work happens in a possession context). */
const ACADEMY_TO_GRASSROOTS: Record<PhaseOfPlay, GrassrootsPhase> = {
  attacking: 'in_possession',
  defending: 'out_of_possession',
  transitions: 'transition',
  individual: 'in_possession',
};

export function projectToGrassrootsPhase(p: PhaseOfPlay): GrassrootsPhase {
  return ACADEMY_TO_GRASSROOTS[p];
}

/** Default expansion from a grassroots phase to the academy phase used for
 *  storage. Used when a grassroots coach creates a new drill — we still
 *  store the academy-tagged value so an academy coach (or later upgrade
 *  of the same coach) sees it correctly classified. Round-trip preserves
 *  attacking / defending / transitions; individual cannot be recovered. */
const GRASSROOTS_TO_ACADEMY_DEFAULT: Record<GrassrootsPhase, PhaseOfPlay> = {
  in_possession: 'attacking',
  out_of_possession: 'defending',
  transition: 'transitions',
};

export function expandFromGrassrootsPhase(p: GrassrootsPhase): PhaseOfPlay {
  return GRASSROOTS_TO_ACADEMY_DEFAULT[p];
}

/** Tier-aware phase list — for chip strips, filter dropdowns, etc. */
export function phasesForTier(tier: CoachingTier): readonly (PhaseOfPlay | GrassrootsPhase)[] {
  return tier === 'grassroots' ? GRASSROOTS_PHASES : PHASES_OF_PLAY;
}

/** Tier-aware meta lookup — returns the right metadata record for a given
 *  phase identifier in the given tier. Returns null if the phase isn't in
 *  the tier's vocabulary (e.g. asking for `individual` on grassroots). */
export function phaseMetaForTier(
  phase: string,
  tier: CoachingTier,
): PhaseOfPlayMeta | GrassrootsPhaseMeta | null {
  if (tier === 'grassroots') {
    return (GRASSROOTS_PHASE_META as Record<string, GrassrootsPhaseMeta>)[phase] ?? null;
  }
  return (PHASE_OF_PLAY_META as Record<string, PhaseOfPlayMeta>)[phase] ?? null;
}

// ============================================================================
// Grassroots themes — Ben Petty 2026-05-05 (afternoon, note 1).
//
// Per-phase grassroots themes a 7–11 year-old can hold in their head. Each
// theme is BOTH an individual responsibility AND a team responsibility — the
// AI does not need to split coaching points by individual vs collective at
// grassroots; the phase-theme already does that work.
//
// In Ben's order (preserved here — the order encodes a progression: in
// possession goes from "create space" → "support" → "progress the ball"
// → "finish", which is itself a teaching arc).
//
// Constraint: the AI session planner should select 1–2 themes per session,
// never more. See MAX_GRASSROOTS_THEMES_PER_SESSION below.
// ============================================================================

export const GRASSROOTS_PHASE_THEMES: Record<GrassrootsPhase, readonly string[]> = {
  in_possession: ['create-space', 'support', 'progress-ball', 'finish'],
  out_of_possession: ['delay-opponent', 'deny-space', 'compact', 'regain'],
  transition: ['react-quickly', 'recovery-runs', 'furthest-pass-forward'],
};

export interface GrassrootsThemeMeta {
  id: string;
  phase: GrassrootsPhase;
  /** Coach-facing label — capitalised, no jargon. */
  label: string;
  /** One-line description in language a U7–U11 player would understand. */
  description: string;
}

export const GRASSROOTS_THEME_META: Record<string, GrassrootsThemeMeta> = {
  // In possession
  'create-space': {
    id: 'create-space',
    phase: 'in_possession',
    label: 'Create space',
    description: 'Move so the ball can come to you — pull defenders apart.',
  },
  support: {
    id: 'support',
    phase: 'in_possession',
    label: 'Support',
    description: 'Give the player on the ball options — angles, distances, both feet.',
  },
  'progress-ball': {
    id: 'progress-ball',
    phase: 'in_possession',
    label: 'Progress the ball',
    description: 'Move the ball forward — through the lines, past pressure.',
  },
  finish: {
    id: 'finish',
    phase: 'in_possession',
    label: 'Finish',
    description: 'Get a shot off — quality over quantity.',
  },

  // Out of possession
  'delay-opponent': {
    id: 'delay-opponent',
    phase: 'out_of_possession',
    label: 'Delay the opponent',
    description: "Slow them down — buy your team-mates time to get behind the ball.",
  },
  'deny-space': {
    id: 'deny-space',
    phase: 'out_of_possession',
    label: 'Deny space',
    description: 'Block the gaps — make them play sideways or backwards.',
  },
  compact: {
    id: 'compact',
    phase: 'out_of_possession',
    label: 'Compact',
    description: 'Stay close as a team — defenders, midfielders, attackers all together.',
  },
  regain: {
    id: 'regain',
    phase: 'out_of_possession',
    label: 'Regain',
    description: 'Win the ball back — pressure, intercept, tackle.',
  },

  // Transition
  'react-quickly': {
    id: 'react-quickly',
    phase: 'transition',
    label: 'React quickly',
    description: 'The moment we win or lose it — go straight away. No standing still.',
  },
  'recovery-runs': {
    id: 'recovery-runs',
    phase: 'transition',
    label: 'Recovery runs',
    description: "We just lost it — sprint back to get behind the ball.",
  },
  'furthest-pass-forward': {
    id: 'furthest-pass-forward',
    phase: 'transition',
    label: 'Furthest pass forward',
    description: 'We just won it — first thought is the longest forward pass that\'s on.',
  },
};

/** AI session planner ceiling — Ben specifically said never more than 1–2
 *  themes per grassroots session. More themes dilutes the focus and
 *  contradicts the "less is more" archetype (founder sync 2026-04-30). */
export const MAX_GRASSROOTS_THEMES_PER_SESSION = 2;

/** Tier-aware theme list — flattened across phases. Returns the 11 grassroots
 *  themes for grassroots tier; falls back to PHASE_SUB_THEMES for academy
 *  (which has more themes per phase, freer-form). */
export function themesForTier(tier: CoachingTier): readonly string[] {
  if (tier === 'grassroots') {
    return [
      ...GRASSROOTS_PHASE_THEMES.in_possession,
      ...GRASSROOTS_PHASE_THEMES.out_of_possession,
      ...GRASSROOTS_PHASE_THEMES.transition,
    ];
  }
  // Academy — flatten the existing 4-phase sub-themes table.
  return PHASES_OF_PLAY.flatMap((p) => PHASE_SUB_THEMES[p]);
}

/** Tier-aware theme list filtered to a single phase. Returns grassroots
 *  themes for grassroots tier (3 grassroots phases) or academy sub-themes
 *  for academy tier (4 academy phases). Returns empty when the phase isn't
 *  in the tier's vocabulary. */
export function themesForPhaseAtTier(
  phase: string,
  tier: CoachingTier,
): readonly string[] {
  if (tier === 'grassroots') {
    return (GRASSROOTS_PHASE_THEMES as Record<string, readonly string[]>)[phase] ?? [];
  }
  return (PHASE_SUB_THEMES as Record<string, string[]>)[phase] ?? [];
}

/** Validate a list of themes a coach (or AI) is proposing for a grassroots
 *  session. Returns the original list when valid, or a trimmed/filtered
 *  version when not. Specifically:
 *    - drops any theme not in the canonical grassroots list
 *    - caps at MAX_GRASSROOTS_THEMES_PER_SESSION
 *  Returned `removed` lists what was dropped + why so the caller can show
 *  a coach-facing message ("dropped X — not a grassroots theme"). */
export function validateGrassrootsThemes(
  themes: readonly string[],
): { themes: string[]; removed: { theme: string; reason: 'unknown' | 'over-cap' }[] } {
  const removed: { theme: string; reason: 'unknown' | 'over-cap' }[] = [];
  const known: string[] = [];
  for (const t of themes) {
    if (!GRASSROOTS_THEME_META[t]) {
      removed.push({ theme: t, reason: 'unknown' });
      continue;
    }
    known.push(t);
  }
  if (known.length > MAX_GRASSROOTS_THEMES_PER_SESSION) {
    for (const t of known.slice(MAX_GRASSROOTS_THEMES_PER_SESSION)) {
      removed.push({ theme: t, reason: 'over-cap' });
    }
  }
  return {
    themes: known.slice(0, MAX_GRASSROOTS_THEMES_PER_SESSION),
    removed,
  };
}
